import type { LatLon, RainCell } from "@radar-sim/schema";
import { distanceNm, bearingDeg } from "@radar-sim/engine";

/**
 * Compute how much of the line of sight from `own` to `target` is blocked
 * by a rain cell. Returns the fraction (0..1) of the cell's footprint along
 * the bearing. Used to attenuate target paint intensity.
 *
 * Geometry is approximate — small-area flat plane around own. Adequate for
 * tactical-radar visualisation at the ranges we care about (≤ 24 nm).
 */
export function rainBlockingFraction(
  own: LatLon,
  target: LatLon,
  rain: ReadonlyArray<RainCell>,
): number {
  if (rain.length === 0) return 0;
  const targetRange = distanceNm(own, target);
  if (targetRange === 0) return 0;
  const targetBrg = bearingDeg(own, target);
  const targetX = targetRange * Math.sin((targetBrg * Math.PI) / 180);
  const targetY = targetRange * Math.cos((targetBrg * Math.PI) / 180);

  let totalChord = 0;
  for (const cell of rain) {
    const cellRange = distanceNm(own, cell.centre);
    const cellBrg = bearingDeg(own, cell.centre);
    const cx = cellRange * Math.sin((cellBrg * Math.PI) / 180);
    const cy = cellRange * Math.cos((cellBrg * Math.PI) / 180);

    // Closest point of cell centre to the line own→target (segment).
    const t = (cx * targetX + cy * targetY) / (targetX * targetX + targetY * targetY);
    if (t <= 0) continue; // cell behind own
    if (t >= 1) continue; // cell beyond target — no obstruction
    const closestX = t * targetX;
    const closestY = t * targetY;
    const dist = Math.sqrt((cx - closestX) ** 2 + (cy - closestY) ** 2);
    if (dist >= cell.radiusNm) continue; // line misses cell

    // Chord length through the cell, normalised by 2 * radius (so a
    // dead-centre crossing = 1.0).
    const chord = 2 * Math.sqrt(cell.radiusNm * cell.radiusNm - dist * dist);
    totalChord += (chord / (2 * cell.radiusNm)) * (cell.intensity / 5);
  }
  return Math.max(0, Math.min(1, totalChord));
}
